import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/api";
import { downloadFile, fetchBlob } from "../../lib/files";
import { PdfCanvas } from "./PdfCanvas";

export const FileViewer = ({ file, onClose, basePath = "/documents", canDownload }) => {
  const [blob, setBlob] = useState(null);
  const [view, setView] = useState(null);
  const [error, setError] = useState(false);
  const onError = useCallback(() => setError(true), []);

  useEffect(() => {
    setBlob(null);
    setView(null);
    setError(false);
    if (!file) return undefined;
    let alive = true;
    api.get(`${basePath}/${file.id}/render`).then(async (r) => {
      if (!alive) return;
      setView(r.data);
      if (["pdf", "image"].includes(r.data.kind)) {
        const b = await fetchBlob(file.id, "view", basePath).catch(() => null);
        if (alive) (b ? setBlob(b) : setError(true));
      }
    }).catch(() => alive && setError(true));
    return () => { alive = false; };
  }, [file, basePath]);

  const url = useMemo(() => (blob && view?.kind === "image" ? URL.createObjectURL(blob) : null), [blob, view]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  const ready = view && (!["pdf", "image"].includes(view.kind) || blob);

  return (
    <Dialog open={!!file} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[90vh] max-w-5xl flex-col gap-3 p-4 sm:p-6" data-testid="file-viewer-dialog" onContextMenu={(e) => !canDownload && e.preventDefault()}>
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="truncate text-base font-bold text-[#182235]" data-testid="file-viewer-title">{file?.original_filename}</DialogTitle>
          <DialogDescription className="text-xs text-[#5b667a]">Visor de documento</DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 items-start justify-center overflow-auto rounded-[12px] border border-[#e8edf5] bg-[#f5f8fc] select-none">
          {error && <p className="m-auto text-sm font-semibold text-[#b42318]" data-testid="file-viewer-error">Documento no disponible por el momento</p>}
          {!error && !ready && <Loader2 className="m-auto h-6 w-6 animate-spin text-[#0f5ef7]" />}
          {!error && ready && view.kind === "pdf" && <PdfCanvas blob={blob} onError={onError} />}
          {!error && ready && view.kind === "image" && url && <img src={url} alt={file?.original_filename} draggable={false} className="m-auto max-h-full max-w-full object-contain" data-testid="file-viewer-image" />}
          {!error && ready && view.kind === "html" && <div className="tr-doc-html w-full bg-white p-6 text-sm" data-testid="file-viewer-html" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(view.html) }} />}
          {!error && ready && view.kind === "text" && <pre className="w-full whitespace-pre-wrap bg-white p-6 text-sm" data-testid="file-viewer-text">{view.text}</pre>}
          {!error && ready && view.kind === "list" && (
            <div className="w-full bg-white p-6" data-testid="file-viewer-list">
              <p className="mb-2 text-sm font-bold">Contenido del archivo comprimido ({view.entries.length})</p>
              <ul className="space-y-0.5 font-mono text-xs">{view.entries.map((e) => <li key={e}>{e}</li>)}</ul>
            </div>
          )}
          {!error && ready && view.kind === "none" && <p className="m-auto text-sm text-[#5b667a]" data-testid="file-viewer-none">Este tipo de archivo no se puede mostrar en el visor.</p>}
        </div>
        {canDownload && <div className="flex justify-end">
          <button type="button" onClick={() => downloadFile(file, basePath)} className="tr-btn-primary px-4 py-2 text-sm" data-testid="file-viewer-download">
            <Download className="h-4 w-4" /> Descargar
          </button>
        </div>}
      </DialogContent>
    </Dialog>
  );
};
